
import {
  computed, defineComponent, PropType, reactive, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
// Direct import (not the `shared` barrel): the barrel re-exports types from
// .vue files, which ts-jest cannot resolve through the *.vue shim — importing
// the component directly keeps this unit-testable. Same component at runtime.
import ButtonGroup from '@/components/flows/operators/shared/fields/ButtonGroup.vue';
import type { FlowScheduleRule } from '@/types/flow-settings';

type RuleUiType = 'dateRange' | 'weekly';
type YearOption = 'current' | 'everyYear';

interface LocalState {
  effect: 'include' | 'exclude';
  ruleType: RuleUiType;
  yearOption: YearOption;
  // "current" (once) → absolute date+time bounds (continuous window)
  startDateTime: Date | null;
  endDateTime: Date | null;
  // "everyYear" → month index (0-11) + day + optional bound times
  startMonthIndex: number | null;
  startDay: number | null;
  endMonthIndex: number | null;
  endDay: number | null;
  yearlyStartTime: Date | null;
  yearlyEndTime: Date | null;
  restrictDays: boolean;
  weekdays: number[];
  restrictTime: boolean;
  timeStartDate: Date | null;
  timeEndDate: Date | null;
}

const pad2 = (n: number | string): string => String(n).padStart(2, '0');

const dateToLocalDateTime = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const parseOnceToDate = (raw: string, isEnd = false): Date => {
  const [datePart, timePart] = raw.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  if (timePart) {
    const [h, mi] = timePart.split(':').map(Number);
    return new Date(y, mo - 1, d, h, mi);
  }
  // Date-only bound (legacy / migrated): default to the very start / very end
  // of the day so a migrated "whole day" range keeps its meaning once edited.
  return isEnd ? new Date(y, mo - 1, d, 23, 59) : new Date(y, mo - 1, d, 0, 0);
};

const dateToHhmm = (d: Date): string => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const hhmmToDate = (hm: string): Date => {
  const [h, mi] = hm.split(':').map(Number);
  const d = new Date();
  d.setHours(h || 0, mi || 0, 0, 0);
  return d;
};

export default defineComponent({
  name: 'ScheduleRuleEditor',

  components: { Dropdown, Calendar, ButtonGroup },

  props: {
    rule: {
      type: Object as PropType<FlowScheduleRule>,
      required: true,
    },
  },

  emits: ['update', 'remove'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Refs to the PrimeVue Calendar overlays so the footer "validate" button
    // can close them (they otherwise only close on outside-click).
    const startCal = ref<any>(null);
    const endCal = ref<any>(null);
    const yearlyStartCal = ref<any>(null);
    const yearlyEndCal = ref<any>(null);
    const timeStartCal = ref<any>(null);
    const timeEndCal = ref<any>(null);
    const closeCal = (cal: any) => {
      if (!cal) return;
      // eslint-disable-next-line no-param-reassign
      cal.overlayVisible = false;
      // Blur so the input's showOnFocus does not immediately re-open the panel.
      const active = document.activeElement as HTMLElement | null;
      if (active) active.blur();
    };

    // `as unknown as LocalState`: reactive() unwraps nested `Date` into a
    // structural type the compiler no longer treats as `Date`, even though it
    // *is* one at runtime. Pin the type back so date helpers accept the fields.
    const local = reactive<LocalState>({
      effect: 'include',
      ruleType: 'dateRange',
      yearOption: 'current',
      startDateTime: null,
      endDateTime: null,
      startMonthIndex: null,
      startDay: null,
      endMonthIndex: null,
      endDay: null,
      yearlyStartTime: null,
      yearlyEndTime: null,
      restrictDays: false,
      weekdays: [],
      restrictTime: false,
      timeStartDate: null,
      timeEndDate: null,
    }) as unknown as LocalState;

    const isOnce = computed(() => local.ruleType === 'dateRange' && local.yearOption === 'current');

    // Guard so re-syncing from the prop never re-emits (would loop).
    let suspend = false;

    const syncFromRule = () => {
      suspend = true;
      const r = props.rule;
      local.effect = r.effect === 'exclude' ? 'exclude' : 'include';
      local.ruleType = !r.date && (r.weekdays?.length || r.time) ? 'weekly' : 'dateRange';
      // New rules (no date yet) default to "current year"; only an explicit
      // yearly rule shows "every year".
      local.yearOption = r.date?.mode === 'yearly' ? 'everyYear' : 'current';

      local.startDateTime = r.date?.mode === 'once' ? parseOnceToDate(r.date.start) : null;
      local.endDateTime = r.date?.mode === 'once' ? parseOnceToDate(r.date.end, true) : null;

      if (r.date?.mode === 'yearly') {
        const [startMd, startTime] = r.date.start.split('T');
        const [endMd, endTime] = r.date.end.split('T');
        const [sm, sd] = startMd.split('-').map(Number);
        const [em, ed] = endMd.split('-').map(Number);
        local.startMonthIndex = sm - 1;
        local.startDay = sd;
        local.endMonthIndex = em - 1;
        local.endDay = ed;
        // Day-only bounds (legacy shape) = full-day window 00:00 → 23:59.
        local.yearlyStartTime = hhmmToDate(startTime ?? '00:00');
        local.yearlyEndTime = hhmmToDate(endTime ?? '23:59');
      } else {
        local.startMonthIndex = null;
        local.startDay = null;
        local.endMonthIndex = null;
        local.endDay = null;
        local.yearlyStartTime = hhmmToDate('00:00');
        local.yearlyEndTime = hhmmToDate('23:59');
      }

      local.restrictDays = !!(r.weekdays && r.weekdays.length);
      local.weekdays = r.weekdays ? [...r.weekdays] : [];
      local.restrictTime = !!r.time;
      local.timeStartDate = hhmmToDate(r.time?.start ?? '09:00');
      local.timeEndDate = hhmmToDate(r.time?.end ?? '18:00');
      suspend = false;
    };

    watch(() => props.rule.id, syncFromRule, { immediate: true });

    const buildRule = (): FlowScheduleRule => {
      const rule: FlowScheduleRule = { id: props.rule.id, effect: local.effect };

      if (local.ruleType === 'dateRange') {
        if (local.yearOption === 'current') {
          if (local.startDateTime && local.endDateTime) {
            rule.date = {
              mode: 'once',
              start: dateToLocalDateTime(local.startDateTime),
              end: dateToLocalDateTime(local.endDateTime),
            };
          }
        } else if (
          local.startMonthIndex != null && local.startDay != null
          && local.endMonthIndex != null && local.endDay != null
        ) {
          // Full-day defaults (00:00 / 23:59) keep the legacy day-only `MM-DD`
          // shape; an explicit time upgrades the bound to `MM-DDTHH:mm`.
          const startTime = local.yearlyStartTime ? dateToHhmm(local.yearlyStartTime) : '00:00';
          const endTime = local.yearlyEndTime ? dateToHhmm(local.yearlyEndTime) : '23:59';
          const startMd = `${pad2(local.startMonthIndex + 1)}-${pad2(local.startDay)}`;
          const endMd = `${pad2(local.endMonthIndex + 1)}-${pad2(local.endDay)}`;
          rule.date = {
            mode: 'yearly',
            start: startTime === '00:00' ? startMd : `${startMd}T${startTime}`,
            end: endTime === '23:59' ? endMd : `${endMd}T${endTime}`,
          };
        }
      }

      // Weekday / daily-time only apply to recurring rules, never to a
      // continuous "once" window.
      if (!isOnce.value) {
        if ((local.ruleType === 'weekly' || local.restrictDays) && local.weekdays.length) {
          rule.weekdays = [...local.weekdays].sort((a, b) => a - b);
        }
        if (local.restrictTime && local.timeStartDate && local.timeEndDate) {
          rule.time = { start: dateToHhmm(local.timeStartDate), end: dateToHhmm(local.timeEndDate) };
        }
      }

      return rule;
    };

    // Emit on any local change (synchronous flush + guard = no feedback loop).
    watch(local, () => {
      if (!suspend) emit('update', buildRule());
    }, { deep: true, flush: 'sync' });

    // NB: switching "Année courante" ⇄ "Chaque année" does NOT reset anything.
    // The two modes hold their draft in SEPARATE fields (startDateTime/endDateTime
    // vs month/day indexes) and buildRule only reads the active mode's fields, so
    // toggling back and forth preserves each mode's input.

    const toggleWeekday = (value: number) => {
      const idx = local.weekdays.indexOf(value);
      if (idx === -1) local.weekdays.push(value);
      else local.weekdays.splice(idx, 1);
    };

    /* ── options ── */
    const effectOptions = computed(() => [
      { value: 'include', label: t('automatedScenarios.globalSettings.schedule.effect.include') },
      { value: 'exclude', label: t('automatedScenarios.globalSettings.schedule.effect.exclude') },
    ]);
    const typeOptions = computed(() => [
      { value: 'dateRange', label: t('automatedScenarios.globalSettings.schedule.type.dateRange') },
      { value: 'weekly', label: t('automatedScenarios.globalSettings.schedule.type.weekly') },
    ]);
    const yearOptions = computed(() => [
      { value: 'current', label: t('automatedScenarios.globalSettings.schedule.yearOption.current') },
      { value: 'everyYear', label: t('automatedScenarios.globalSettings.schedule.yearOption.everyYear') },
    ]);

    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    const monthOptions = computed(() => monthKeys.map((key, i) => ({ value: i, label: t(`months.${key}`) })));

    const getDaysInMonth = (monthIndex: number): number => new Date(2024, monthIndex + 1, 0).getDate();
    const dayOptionsFor = (monthIndex: number | null) => {
      const count = monthIndex == null ? 31 : getDaysInMonth(monthIndex);
      return Array.from({ length: count }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
    };
    const startDayOptions = computed(() => dayOptionsFor(local.startMonthIndex));
    const endDayOptions = computed(() => dayOptionsFor(local.endMonthIndex));

    const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const weekdayChips = computed(() => weekdayKeys.map((key, i) => ({
      value: i + 1, // ISO 1..7
      label: t(`automatedScenarios.globalSettings.schedule.weekdays.${key}`),
    })));

    /* ── validation ── */
    const isEndAfterStart = computed(() => {
      if (local.ruleType !== 'dateRange') return true;
      if (local.yearOption === 'current') {
        if (!local.startDateTime || !local.endDateTime) return true;
        return local.endDateTime.getTime() >= local.startDateTime.getTime();
      }
      if (local.startMonthIndex == null || local.startDay == null
        || local.endMonthIndex == null || local.endDay == null) return true;
      const startTime = local.yearlyStartTime ? dateToHhmm(local.yearlyStartTime) : '00:00';
      const endTime = local.yearlyEndTime ? dateToHhmm(local.yearlyEndTime) : '23:59';
      const s = `${pad2(local.startMonthIndex + 1)}-${pad2(local.startDay)}T${startTime}`;
      const e = `${pad2(local.endMonthIndex + 1)}-${pad2(local.endDay)}T${endTime}`;
      return e >= s;
    });

    /* ── human summary ── */
    const formatDateTime = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

    const summary = computed(() => {
      const parts: string[] = [];

      if (local.ruleType === 'dateRange') {
        if (local.yearOption === 'current') {
          if (local.startDateTime && local.endDateTime) {
            parts.push(t('automatedScenarios.globalSettings.schedule.summary.dateOnce', {
              start: formatDateTime(local.startDateTime), end: formatDateTime(local.endDateTime),
            }));
          } else {
            parts.push(t('automatedScenarios.globalSettings.schedule.summary.incomplete'));
          }
        } else if (local.startMonthIndex != null && local.startDay != null
          && local.endMonthIndex != null && local.endDay != null) {
          const startTime = local.yearlyStartTime ? dateToHhmm(local.yearlyStartTime) : '00:00';
          const endTime = local.yearlyEndTime ? dateToHhmm(local.yearlyEndTime) : '23:59';
          // Show the times only when the window is not the full-day default.
          const withTimes = startTime !== '00:00' || endTime !== '23:59';
          parts.push(t('automatedScenarios.globalSettings.schedule.summary.dateYearly', {
            start: `${pad2(local.startDay)}/${pad2(local.startMonthIndex + 1)}${withTimes ? ` ${startTime}` : ''}`,
            end: `${pad2(local.endDay)}/${pad2(local.endMonthIndex + 1)}${withTimes ? ` ${endTime}` : ''}`,
          }));
        } else {
          parts.push(t('automatedScenarios.globalSettings.schedule.summary.incomplete'));
        }
      } else {
        parts.push(t('automatedScenarios.globalSettings.schedule.summary.weekly'));
      }

      // Weekday / time never decorate a continuous "once" window.
      if (!isOnce.value) {
        const activeDays = local.ruleType === 'weekly' || local.restrictDays ? local.weekdays : [];
        if (activeDays.length && activeDays.length < 7) {
          const names = [...activeDays].sort((a, b) => a - b)
            .map((d) => t(`automatedScenarios.globalSettings.schedule.weekdays.${weekdayKeys[d - 1]}`));
          parts.push(names.join(', '));
        }

        if (local.restrictTime && local.timeStartDate && local.timeEndDate) {
          parts.push(t('automatedScenarios.globalSettings.schedule.summary.time', {
            start: dateToHhmm(local.timeStartDate), end: dateToHhmm(local.timeEndDate),
          }));
        }
      }

      return parts.join(' — ');
    });

    return {
      t,
      local,
      isOnce,
      startCal,
      endCal,
      yearlyStartCal,
      yearlyEndCal,
      timeStartCal,
      timeEndCal,
      closeCal,
      effectOptions,
      typeOptions,
      yearOptions,
      monthOptions,
      startDayOptions,
      endDayOptions,
      weekdayChips,
      toggleWeekday,
      isEndAfterStart,
      summary,
    };
  },
});
